import classNames from 'classnames/bind';
import {
  Field,
  RichText,
  Text,
  LinkField,
  withDatasourceCheck,
  useSitecoreContext,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ComponentProps } from 'lib/component-props';
import type { ResponsiveImageFields } from 'src/models/responsive-image';
import Icon from 'components/helpers/Icon';
import Link from 'components/helpers/Link/Link';
import { ButtonVariant } from 'components/helpers/Button/Button';
import styles from './SignIn5050.module.scss';
import compose from 'lib/enhancers/compose';
import withStyles from 'lib/enhancers/withStyles';
import { ChangeEventHandler, MouseEventHandler, useState } from 'react';

const cx = classNames.bind(styles);

type SignIn5050Props = ComponentProps & {
  fields: ResponsiveImageFields & {
    signInButtonLabel: Field<string>;
    sampleBody: Field<string>;
    signInTitle: Field<string>;
    content: Field<string>;
    usernameFieldLabel: Field<string>;
    errorText: Field<string>;
    customerType: CustomerType;
  };
  rendering: { uid: string };
};

export type CustomerType = {
  fields: {
    dropdownLabel: Field<string>;
    portalDestinationLink: LinkField;
    forgotPasswordLink: LinkField;
    registerLink: LinkField;
    registerLinkText: Field<string>;
    forgotPasswordShortInstructions: Field<string>;
    forgotPasswordLongInstructions: Field<string>;
  };
};

const SignIn5050 = (props: SignIn5050Props): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const { pageEditing } = sitecoreContext;
  const { rendering, className } = props;
  const [userNameValue, setuserNameValue] = useState<string>('');
  const usernameHref = `${props.fields?.customerType?.fields?.portalDestinationLink?.value?.href}${userNameValue}`;
  const [showError, setShowError] = useState<boolean>(false);
  const showGreenBorder = props.params.greenBorder === '1';
  const reverseForm = props.params.presentation === 'Form Right';
  // get rendering Parameters

  const handleClick: MouseEventHandler<HTMLAnchorElement> = (e) => {
    if (!userNameValue) {
      e.preventDefault();
      setShowError(true);
    }
  };

  const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setShowError(!e.target.value);
    setuserNameValue(e.target.value);
  };

  return (
    <div
      className={cx({
        'sign-in-5050-container': true,
        'reverse-form': reverseForm,
        className,
      })}
    >
      <div className={cx({ 'sign-in-50-50-form': true, 'green-border': showGreenBorder })}>
        <Icon variant="person" className={styles['sign-in-50-50-form__icon']} />
        <Text tag="h3" field={props.fields?.signInTitle} encode={pageEditing} />
        <label>
          <Text
            onClick={handleClick}
            field={props.fields?.usernameFieldLabel}
            value={userNameValue}
            autocomplete={true}
            encode={pageEditing}
          />
        </label>
        <input onInput={handleChange} autoComplete="username" value={userNameValue} type="text" id={rendering.uid} />
        {showError && (
          <div className={styles['sign-in-50-50-form__errormsg']} role="alert">
            <Icon variant="error" className={styles['sign-in-50-50-form__warning_icon']} />
            <label>
              <Text field={props.fields?.errorText} encode={pageEditing} />
            </label>
          </div>
        )}
        {props.fields?.customerType?.fields?.portalDestinationLink?.value?.url !== '' && (
          <Link
            onClick={handleClick}
            field={{
              ...props.fields?.customerType?.fields?.portalDestinationLink,
              value: { ...props.fields?.customerType?.fields?.portalDestinationLink?.value, href: usernameHref },
            }}
            showLinkTextWithChildrenPresent={false}
            variant={ButtonVariant.Primary}
            className={styles['sign-in-50-50-form__submit']}
          >
            <Text field={props.fields?.signInButtonLabel} encode={pageEditing} />
          </Link>
        )}
        {props.fields?.customerType && (
          <Link
            field={props.fields?.customerType?.fields?.forgotPasswordLink}
            className={styles['sign-in-50-50-form__forgotPasswordLink']}
          />
        )}
        <div className={styles['sign-in-50-50-form__instructions']}>
          <RichText field={props.fields?.customerType?.fields?.forgotPasswordLongInstructions} />
        </div>
      </div>
      <div className={styles['sign-in-5050-content']}>
        <RichText field={props.fields?.content} />
        {props.fields?.customerType && (
          <Link
            field={props.fields?.customerType.fields?.registerLink}
            className={styles['sign-in-5050-content__registerLink']}
            icon="carat"
            showLinkTextWithChildrenPresent={false}
          >
            <Text field={props.fields?.customerType.fields.registerLinkText} encode={pageEditing} />
          </Link>
        )}
      </div>
    </div>
  );
};

export default compose<SignIn5050Props>(withDatasourceCheck(), withStyles())(SignIn5050);
